import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { IPspLoginInfo, ITypeListItem } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-provider-login-info',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        ConfirmPopupModule,
        FormsModule,
        ReactiveFormsModule,
        Select,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './provider-login-info.component.html',
    styleUrl: './provider-login-info.component.scss'
})
export class ProviderLoginInfoComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    loginInfoList: IPspLoginInfo[] = [];
    pspTypes: ITypeListItem[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedLoginInfo: IPspLoginInfo | null = null;
    submitted = false;
    loginForm!: FormGroup;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.loadLoginInfo();
        this.loadDropdowns();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadLoginInfo(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.paymentService.getLoginInfo().subscribe({
            next: (res) => {
                this.loginInfoList = res.pspLoginInfo || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    loadDropdowns(): void {
        this.paymentService.getPspTypeInformation().subscribe({
            next: (res) => {
                this.pspTypes = res.typeList || [];
            }
        });
    }

    initializeForm(): void {
        this.loginForm = this.fb.group({
            pspId: [null, [Validators.required]],
            deepLink: ['', [Validators.required]],
            userName: ['', [Validators.required]],
            password: ['', [Validators.required]],
            additionalInfo: ['']
        });
    }

    isInvalid(field: string): boolean {
        const control = this.loginForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedLoginInfo = null;
        this.submitted = false;
        this.loginForm.reset({ pspId: null, deepLink: '', userName: '', password: '', additionalInfo: '' });
        this.dialogVisible = true;
    }

    openEdit(info: IPspLoginInfo): void {
        this.isEditMode = true;
        this.selectedLoginInfo = info;
        this.submitted = false;
        this.loginForm.patchValue({
            pspId: info.pspTypeId,
            deepLink: info.deepLink,
            userName: info.userName,
            password: info.password,
            additionalInfo: info.additionalInfo
        });
        this.dialogVisible = true;
    }

    confirmDelete(event: Event, info: IPspLoginInfo): void {
        this.confirmationService.confirm({
            target: event.target as HTMLElement,
            message: 'Are you sure you want to delete this login info?',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonProps: { severity: 'danger' },
            accept: () => this.onDelete(info)
        });
    }

    onDelete(info: IPspLoginInfo): void {
        if (!info?.id) return;
        this.paymentService.deleteLoginInfo(info.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Login info has been deleted successfully.' });
                this.loadLoginInfo();
            }
        });
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    saveLoginInfo(): void {
        this.submitted = true;
        if (this.loginForm.invalid) {
            return;
        }

        const formData = this.loginForm.value;
        const request = {
            pspId: formData.pspId,
            deepLink: formData.deepLink,
            userName: formData.userName,
            password: formData.password,
            additionalInfo: formData.additionalInfo
        };

        if (!this.isEditMode) {
            this.paymentService.createLoginInfo(request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Login info has been created successfully.'
                    });
                    this.loadLoginInfo();
                    this.dialogVisible = false;
                }
            });
        } else if (this.selectedLoginInfo) {
            this.paymentService.updateLoginInfo(this.selectedLoginInfo.id, request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Login info has been updated successfully.'
                    });
                    this.loadLoginInfo();
                    this.dialogVisible = false;
                }
            });
        }
    }
}
